import React, { useMemo, useState } from 'react';
import { Calendar, ChevronUp, ChevronDown } from 'lucide-react';

interface TimelineBarProps {
    transactions: Array<{
        timestamp: Date;
        from: string;
        to: string;
        amount: number;
        currency: string; // Token symbol (ETH, USDT, TRX, etc.)
        txHash: string;
    }>;
    currentIndex: number;
    onSeek?: (index: number) => void;
}

/** Axis date — day + month + year, locale-stable for LE reports. */
export function formatTimelineAxisDate(d: Date): string {
    return d.toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
    });
}

/** Axis time — hour:minute only (block time, local tz). */
export function formatTimelineAxisTime(d: Date): string {
    return d.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
    });
}

/**
 * Marker left % with edge-safe transform so dots at 0%/100% stay inside the track.
 */
export function timelineMarkerStyle(positionPct: number): React.CSSProperties {
    const clamped = Math.max(0, Math.min(100, positionPct));
    let transform = 'translateX(-50%)';
    if (clamped <= 0.5) transform = 'translateX(0)';
    else if (clamped >= 99.5) transform = 'translateX(-100%)';
    return { left: `${clamped}%`, transform };
}

export const TimelineBar: React.FC<TimelineBarProps> = ({ transactions, currentIndex, onSeek }) => {
    const [isCollapsed, setIsCollapsed] = useState(false);

    const axis = useMemo(() => {
        if (transactions.length === 0) return null;
        const start = transactions[0].timestamp;
        const end = transactions[transactions.length - 1].timestamp;
        const current =
            currentIndex >= 0 && currentIndex < transactions.length
                ? transactions[currentIndex].timestamp
                : null;
        const rangeMs = end.getTime() - start.getTime();
        const showCurrent =
            current != null &&
            currentIndex > 0 &&
            currentIndex < transactions.length - 1;
        return { start, end, current, rangeMs, showCurrent };
    }, [transactions, currentIndex]);

    if (transactions.length === 0 || !axis) return null;

    // Collapsed mini view - just show current tx info
    if (isCollapsed) {
        return (
            <div className="bg-card/95 border border-border/80 rounded-lg px-3 py-2 shadow-xl">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                        <Calendar className="w-4 h-4 text-primary shrink-0" />
                        <span className="text-xs text-foreground/80 shrink-0">Timeline</span>
                        {currentIndex >= 0 && currentIndex < transactions.length && (
                            <>
                                <span className="text-xs text-emerald-700 dark:text-emerald-400 font-mono shrink-0">
                                    {transactions[currentIndex].amount.toLocaleString()}{' '}
                                    {transactions[currentIndex].currency}
                                </span>
                                <span
                                    className="text-xs text-muted-foreground font-mono truncate"
                                    title="On-chain block time"
                                >
                                    {formatTimelineAxisDate(transactions[currentIndex].timestamp)}{' '}
                                    {formatTimelineAxisTime(transactions[currentIndex].timestamp)}
                                </span>
                            </>
                        )}
                    </div>
                    <button
                        type="button"
                        onClick={() => setIsCollapsed(false)}
                        className="p-1 hover:bg-muted rounded text-muted-foreground flex items-center gap-1 shrink-0"
                        title="Expand timeline"
                    >
                        <span className="text-xs">Details</span>
                        <ChevronUp className="w-4 h-4" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-card/95 border border-border/80 rounded-lg p-3 shadow-xl" data-testid="transaction-timeline">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 min-w-0">
                    <Calendar className="w-4 h-4 text-primary shrink-0" />
                    <h3 className="text-xs font-semibold text-foreground">Transaction Timeline</h3>
                    <span
                        className="text-xs text-muted-foreground font-normal truncate"
                        title="Marker times are blockchain confirmation timestamps."
                    >
                        on-chain block times
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => setIsCollapsed(true)}
                    className="p-1 hover:bg-muted rounded text-muted-foreground shrink-0"
                    title="Collapse timeline"
                >
                    <ChevronDown className="w-4 h-4" />
                </button>
            </div>

            {/* Track + markers (no floating date labels — those live on the axis row) */}
            <div className="space-y-2" data-testid="timeline-track">
                <div className="h-2 bg-muted rounded-full relative overflow-hidden">
                    <div
                        className="absolute inset-y-0 left-0 bg-gradient-to-r from-primary to-brand-gold-hover transition-all duration-300"
                        style={{
                            width: `${currentIndex >= 0 ? ((currentIndex + 1) / transactions.length) * 100 : 0}%`,
                        }}
                    />
                </div>

                <div className="relative h-4" role="list" aria-label="Transaction markers">
                    {transactions.map((tx, idx) => {
                        const position =
                            axis.rangeMs > 0
                                ? ((tx.timestamp.getTime() - axis.start.getTime()) / axis.rangeMs) * 100
                                : (idx / Math.max(transactions.length - 1, 1)) * 100;

                        const isPast = idx <= currentIndex;
                        const isCurrent = idx === currentIndex;

                        return (
                            <button
                                type="button"
                                key={tx.txHash}
                                role="listitem"
                                className="absolute top-1/2 -mt-1.5 p-0 border-0 bg-transparent cursor-pointer z-[1]"
                                style={timelineMarkerStyle(position)}
                                onClick={() => onSeek?.(idx)}
                                title={`Jump to ${formatTimelineAxisDate(tx.timestamp)} ${formatTimelineAxisTime(tx.timestamp)} (on-chain)`}
                                aria-label={`Transfer ${idx + 1} of ${transactions.length}, ${formatTimelineAxisDate(tx.timestamp)}`}
                                aria-current={isCurrent ? 'step' : undefined}
                            >
                                <div
                                    className={`w-3 h-3 rounded-full border-2 transition-all ${
                                        isCurrent
                                            ? 'bg-amber-500 border-amber-400 dark:bg-yellow-400 dark:border-yellow-300 scale-125 shadow-md shadow-amber-500/40'
                                            : isPast
                                              ? 'bg-primary border-primary'
                                              : 'bg-secondary border-border/60'
                                    }`}
                                />
                            </button>
                        );
                    })}
                </div>

                {/* Fixed axis: Start | Current | End — never absolute-stacked */}
                <div
                    className="grid grid-cols-3 gap-2 text-[11px] leading-tight font-mono text-muted-foreground"
                    data-testid="timeline-axis"
                >
                    <div className="min-w-0 text-left" data-testid="timeline-axis-start">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground/80 font-sans mb-0.5">
                            Start
                        </div>
                        <div className="text-foreground/90 truncate">{formatTimelineAxisDate(axis.start)}</div>
                        <div className="truncate">{formatTimelineAxisTime(axis.start)}</div>
                    </div>

                    <div className="min-w-0 text-center" data-testid="timeline-axis-current">
                        {axis.showCurrent && axis.current ? (
                            <>
                                <div className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400 font-sans mb-0.5">
                                    Current
                                </div>
                                <div className="text-foreground/90 truncate">
                                    {formatTimelineAxisDate(axis.current)}
                                </div>
                                <div className="truncate">{formatTimelineAxisTime(axis.current)}</div>
                            </>
                        ) : (
                            <div className="text-[10px] uppercase tracking-wide text-muted-foreground/60 font-sans pt-4">
                                {transactions.length} tx{transactions.length === 1 ? '' : 's'}
                            </div>
                        )}
                    </div>

                    <div className="min-w-0 text-right" data-testid="timeline-axis-end">
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground/80 font-sans mb-0.5">
                            End
                        </div>
                        <div className="text-foreground/90 truncate">{formatTimelineAxisDate(axis.end)}</div>
                        <div className="truncate">{formatTimelineAxisTime(axis.end)}</div>
                    </div>
                </div>
            </div>

            {currentIndex >= 0 && currentIndex < transactions.length && (
                <div className="mt-3 p-2 bg-muted/50 rounded-lg border border-border/80">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                        <div className="min-w-0">
                            <span className="text-muted-foreground">From:</span>
                            <p className="font-mono text-xs text-foreground truncate">
                                {transactions[currentIndex].from.substring(0, 8)}...
                            </p>
                        </div>
                        <div className="min-w-0">
                            <span className="text-muted-foreground">To:</span>
                            <p className="font-mono text-xs text-foreground truncate">
                                {transactions[currentIndex].to.substring(0, 8)}...
                            </p>
                        </div>
                        <div className="min-w-0">
                            <span className="text-muted-foreground">Amount:</span>
                            <p className="font-semibold text-emerald-700 dark:text-emerald-400">
                                {transactions[currentIndex].amount.toLocaleString()}{' '}
                                {transactions[currentIndex].currency}
                            </p>
                        </div>
                        <div className="min-w-0">
                            <span className="text-muted-foreground" title="Blockchain confirmation time">
                                On-chain:
                            </span>
                            <p className="text-foreground text-xs font-mono truncate">
                                {formatTimelineAxisDate(transactions[currentIndex].timestamp)}{' '}
                                {formatTimelineAxisTime(transactions[currentIndex].timestamp)}
                            </p>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
